'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ArrowRight, Quote, Star } from 'lucide-react'
import { useState } from 'react'

type Testimonial = {
  id: string | number
  quote: string
  author: string
  role: string
}

interface TestimonialsProps {
  initialTestimonials?: Testimonial[]
}

export default function Testimonials({ initialTestimonials }: TestimonialsProps) {
  const displayTestimonials = initialTestimonials ?? []
  const [current, setCurrent] = useState(0)

  if (displayTestimonials.length === 0) return null

  return (
    <section className="home-section relative overflow-hidden">
      {/* Background */}
      {/* Background - Removed for global unity */}

      <div className="home-container relative z-10">
        {/* 2-Column Layout */}
        {/* ════ Mobile View (< lg) ════ */}
        <div className="mb-12 lg:hidden">
          {/* Header */}
          <div className="mb-8">
            <m.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="home-kicker mb-4 text-purple-400"
            >
              Отзывы
            </m.p>
            <m.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-title mb-12"
            >
              Что говорят
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                наши клиенты
              </span>
            </m.h2>
          </div>

          {/* Carousel -> Vertical Stack */}
          <div className="flex flex-col gap-6">
            {displayTestimonials.map((item) => (
              <div
                key={item.id}
                className="w-full rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8"
              >
                {/* Stars */}
                <div className="mb-4 flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-amber-400" fill="currentColor" />
                  ))}
                </div>
                {/* Quote */}
                <p className="home-body mb-6 leading-relaxed font-light text-white/90">
                  &ldquo;{item.quote}&rdquo;
                </p>
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-sm font-semibold text-white">
                    {item.author.charAt(0)}
                  </div>
                  <div>
                    <p className="home-body font-semibold text-white">{item.author}</p>
                    <p className="home-subnote text-gray-500">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ════ Desktop View (>= lg) ════ */}
        <div className="hidden items-center gap-8 md:gap-16 lg:grid xl:grid-cols-2 xl:gap-24">
          {/* Left - Header & Selector */}
          <div>
            <m.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="home-kicker mb-4 text-purple-400"
            >
              Отзывы
            </m.p>

            <m.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="home-title mb-12"
            >
              Что говорят
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                наши клиенты
              </span>
            </m.h2>

            {/* Author List */}
            <div className="space-y-4">
              {displayTestimonials.map((item, i) => (
                <m.button
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setCurrent(i)}
                  className={`flex w-full items-center gap-4 rounded-2xl p-4 text-left transition-all duration-300 ${
                    current === i
                      ? 'border border-purple-500/30 bg-gradient-to-r from-purple-500/20 to-pink-500/20'
                      : 'border border-transparent bg-white/[0.02] hover:bg-white/[0.05]'
                  }`}
                >
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-semibold transition-all duration-300 ${
                      current === i
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {item.author.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate font-semibold transition-colors duration-300 ${
                        current === i ? 'text-white' : 'text-gray-400'
                      }`}
                    >
                      {item.author}
                    </p>
                    <p className="home-subnote truncate text-gray-500">{item.role}</p>
                  </div>
                  <ArrowRight
                    className={`h-5 w-5 shrink-0 transition-all duration-300 ${
                      current === i ? 'translate-x-1 text-purple-400' : 'text-gray-600'
                    }`}
                  />
                </m.button>
              ))}
            </div>
          </div>

          {/* Right - Quote Card */}
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-4 rounded-[48px] bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 blur-xl" />

            {/* Card */}
            <div className="relative rounded-[40px] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-10 lg:p-14">
              {/* Quote Icon */}
              <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500">
                <Quote className="h-7 w-7 text-white" />
              </div>

              <AnimatePresence mode="wait">
                <m.div
                  key={current}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Stars */}
                  <div className="mb-6 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-amber-400" fill="currentColor" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="home-quote mb-8 font-light text-white/90">
                    &ldquo;{displayTestimonials[current].quote}&rdquo;
                  </p>

                  {/* Author mobile */}
                  <div className="flex items-center gap-3 lg:hidden">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-sm font-semibold">
                      {displayTestimonials[current].author.charAt(0)}
                    </div>
                    <div>
                      <p className="home-body font-semibold text-white">
                        {displayTestimonials[current].author}
                      </p>
                      <p className="home-subnote text-gray-500">
                        {displayTestimonials[current].role}
                      </p>
                    </div>
                  </div>
                </m.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
